import React, { useMemo, useRef, useState } from "react";
import {
    StriimTable,
    StriimTypography,
    StriimBadge,
    StriimChip,
    StriimModal,
    StriimToggleButton
} from "@striim/striim-ui";
import { USER_TABLE_STRINGS, userColumnKey, userTableColumns } from "../../constants";
import { useNavigate } from "react-router-dom";
import { useUserManagement } from "core/user-management/user-management-context";
import { canActivateDeactivateUser, isUserDeletable, isUserEditable, isReservedUser } from "core/user-management/update.control";
import { DeleteModal } from "src/modules/apps/pages/user-management/common";
import { Grid, useTheme } from "@mui/material";
import api from "core/api/api";
import UserManagementApi from "core/user-management/userManagementApi/userManagementApi";
import growl from "app/components/common/growl";

import { DeleteIcon, EditIcon, EyeIcon, ToggledOn, ToggledOff } from "../../../../../../../generic/icon/customIcons";
import { useStyles } from "./user-table.styles";
import TableIconButton from "../../../common/table-icon-button";

const UserTable = ({ users }) => {
    const themeV5 = useTheme();
    const classes = useStyles(themeV5);
    const navigate = useNavigate();

    const { deleteUser, deactivateUser, activateUser } = useUserManagement();
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [disableAlertDialogVisible, setDisableAlertDialogVisible] = useState(false);
    const [alertingStates, setAlertingStates] = useState({});
    const userTableRef = useRef();
    const alertToggleRef = useRef();

    React.useEffect(() => {
        const fetchStates = async () => {
            const states = {};
            for (const u of users || []) {
                const userId = u[USER_TABLE_STRINGS.label.userId];
                const hasGlobalAdmin = u.roles?.some(
                    r => r?.name?.toLowerCase() === "global.admin" || r?.name?.toLowerCase() === "global.saasadmin"
                );
                if (!isReservedUser(userId?.toLowerCase()) && !hasGlobalAdmin) {
                    try {
                        states[userId] = await UserManagementApi.getAlertingPermissionStatus(userId);
                    } catch {
                        states[userId] = false;
                    }
                }
            }
            setAlertingStates(states);
        };
        fetchStates();
    }, [users]);

    const handleAlertToggle = (userId, currentlyEnabled) => {
        if (currentlyEnabled) {
            alertToggleRef.current = userId;
            setDisableAlertDialogVisible(true);
        } else {
            api.setAlertingPermission(userId, true)
                .done(() => {
                    setAlertingStates(prev => ({ ...prev, [userId]: true }));
                    growl.success(`Alerting enabled for ${userId}`, "Success");
                })
                .fail(e => growl.error(e.message || "Failed to enable alerting"));
        }
    };

    const onDisableAlertConfirm = () => {
        const userId = alertToggleRef.current;
        setDisableAlertDialogVisible(false);
        api.setAlertingPermission(userId, false)
            .done(() => {
                setAlertingStates(prev => ({ ...prev, [userId]: false }));
                growl.success(`Alerting disabled for ${userId}`, "Success");
            })
            .fail(e => growl.error(e.message || "Failed to disable alerting"));
    };

    const userTableColumnHeaders = userTableColumns.map(v => {
        return v.accessor === userColumnKey.name
            ? {
                  ...v,
                  Header: (
                      <StriimBadge badgeContent={users.length} inline>
                          <StriimTypography variant="body3" color="greyscale.600">
                              {v.Header}
                          </StriimTypography>
                      </StriimBadge>
                  )
              }
            : v;
    });

    const handleDelete = userId => {
        userTableRef.current = userId;
        setDeleteDialogVisible(true);
    };

    const toggleActivation = user => {
        const userId = user[USER_TABLE_STRINGS.label.userId];
        if (user.isActive) {
            deactivateUser(userId);
        } else {
            activateUser(userId);
        }
    };

    const onDeleteConfirm = () => {
        const userId = userTableRef.current;
        deleteUser(userId);
        setDeleteDialogVisible(false);
    };

    const getFormattedList = (list, type) => {
        const seperator = type === USER_TABLE_STRINGS.label.role ? ";" : ",";
        const listItems = list?.map((v, i) => {
            return i !== 0 ? (
                <React.Fragment key={`${v.name}${i}`}>
                    {seperator} {v.formattedName || v.name}
                </React.Fragment>
            ) : (
                v.formattedName || v.name
            );
        });

        const listCountText = `${list.length.toString()} ${type}${list.length > 1 ? `s` : ``}`;
        const countFormat = <StriimTypography variant="bodySemibold">{listCountText}</StriimTypography>;
        return (
            list.length >= 1 && (
                <>
                    {countFormat} ({listItems})
                </>
            )
        );
    };

    const getOriginType = originType => {
        const ssoChip = (
            <StriimChip type="tag-chip" variant="default" label="SSO" className={classes.ssoChip} hideAvatar />
        );
        const striimChip = (
            <StriimChip type="tag-chip" variant="default" label="STRIIM" className={classes.striimChip} hideAvatar />
        );

        return originType.toLowerCase() === "sso" ? ssoChip : striimChip;
    };

    const userTableData = useMemo(
        () =>
            users?.map(v => {
                const rolesFormatted = getFormattedList(v.roles, USER_TABLE_STRINGS.label.role);
                const permissionsFormatted = getFormattedList(v.permissionsText, USER_TABLE_STRINGS.label.permission);
                const originType = getOriginType(v.originType);
                const rolesPermissions = (
                    <>
                        {rolesFormatted} {permissionsFormatted}
                    </>
                );
                const actions = (
                    <Grid container alignItems="center" justifyContent="flex-end">
                        <Grid item>
                            <TableIconButton
                                navigate={() => navigate(`/users/user/${v[USER_TABLE_STRINGS.label.userId]}`)}
                                tooltipTitle="View"
                                Icon={EyeIcon}
                                dataTestId={`${v[USER_TABLE_STRINGS.label.userId]}-view`}
                            />
                        </Grid>
                        {isUserEditable(v) && (
                            <Grid item>
                                <TableIconButton
                                    navigate={() => navigate(`/users/user/${v[USER_TABLE_STRINGS.label.userId]}/edit`)}
                                    tooltipTitle="Edit"
                                    Icon={EditIcon}
                                    dataTestId={`${v[USER_TABLE_STRINGS.label.userId]}-edit`}
                                />
                            </Grid>
                        )}
                        {isUserDeletable(v) && (
                            <Grid item>
                                <TableIconButton
                                    navigate={() => handleDelete(v[USER_TABLE_STRINGS.label.userId])}
                                    tooltipTitle="Delete"
                                    Icon={DeleteIcon}
                                    dataTestId={`${v[USER_TABLE_STRINGS.label.userId]}-delete`}
                                />
                            </Grid>
                        )}
                        {canActivateDeactivateUser(v) && (
                            <Grid item>
                                <TableIconButton
                                    navigate={() => toggleActivation(v)}
                                    tooltipTitle={v.isActive ? "Deactivate User" : "Activate User"}
                                    Icon={v.isActive ? ToggledOn : ToggledOff}
                                    dataTestId={`${v[USER_TABLE_STRINGS.label.userId]}-${
                                        v.isActive ? "deactivate" : "activate"
                                    }`}
                                />
                            </Grid>
                        )}
                    </Grid>
                );
                const userId = v[USER_TABLE_STRINGS.label.userId];
                const isReserved = isReservedUser(userId?.toLowerCase());
                const hasGlobalAdmin = v.roles?.some(
                    r => r?.name?.toLowerCase() === "global.admin" || r?.name?.toLowerCase() === "global.saasadmin"
                );
                const alertEnabled = alertingStates[userId] ?? false;
                const alertsCell = isReserved || hasGlobalAdmin ? null : (
                    <StriimToggleButton
                        checked={alertEnabled}
                        onChange={() => handleAlertToggle(userId, alertEnabled)}
                        data-test-id={`${userId}-alerts-toggle`}
                    />
                );
                return {
                    [userColumnKey.name]: userId,
                    [userColumnKey.rolesPermissions]: rolesPermissions,
                    [userColumnKey.originType]: originType,
                    [userColumnKey.alerts]: alertsCell,
                    [userColumnKey.actions]: actions
                };
            }),
        [users, alertingStates]
    );

    return (
        <>
            <Grid container>
                <Grid item md={12}>
                    <StriimTable
                        columns={userTableColumnHeaders}
                        data={userTableData}
                        TableProps={{ "data-testid": "users-table" }}
                        indexedRow
                        TableBodyProps={{ seperated: true }}
                        TableHeaderCellProps={{
                            style: {
                                background: themeV5?.palette.greyscale[50]
                            }
                        }}
                    />
                </Grid>
            </Grid>
            <DeleteModal
                id={userTableRef?.current}
                type={"user"}
                deleteDialogVisible={deleteDialogVisible}
                setDeleteDialogVisible={setDeleteDialogVisible}
                onDeleteConfirm={onDeleteConfirm}
            />
            <StriimModal
                size="small"
                isVisible={disableAlertDialogVisible}
                onConfirm={onDisableAlertConfirm}
                onCancel={() => setDisableAlertDialogVisible(false)}
                autoHeight
                variant="error"
                confirmContent="Disable"
                confirmButtonProps={{ "data-test-id": "confirm-disable-alerts-button" }}
                cancelButtonProps={{ "data-test-id": "cancel-disable-alerts-button" }}
                titleContent={
                    <StriimTypography variant="h2" color="primary.700">
                        Disable Alerts
                    </StriimTypography>
                }
            >
                <StriimTypography variant="body4">
                    This will stop all active alerts for this user. Existing alert configurations will be preserved but
                    will not trigger until re-enabled.
                </StriimTypography>
            </StriimModal>
        </>
    );
};

export default UserTable;
